import { BillingFrequencyType, NotesType } from 'Portals/Internal/constants'

export enum BillingFrequencySearchResultFields {
	searchById = 'searchById',
	searchByCompanyId = 'searchByCompanyId',
	searchByCustomerId = 'searchByCustomerId',
	searchByCompanyName = 'searchByCompanyName',
	searchByServiceSite = 'searchByServiceSite',
	searchByCustomerName = 'searchByCustomerName',
	searchByFrequencyCode = 'searchByFrequencyCode',
	searchByBillingAgency = 'searchByBillingAgency'
}

export enum BillingFrequencyLabels {
	ReportId = 'reportId',
	Report = 'report',
	PaymentTermId = 'paymentTermId',
	PaymentTerm = 'paymentTerm',
	GroupByServiceSite = 'groupByServiceSite',
	GroupByPO = 'groupByPO',
	Frequency = 'frequency',
	InvoiceGenerationCode = 'invoiceGenerationCode',
	InvoiceTypeSendCode = 'invoiceTypeSendCode',
	OverrideNextInvoiceDate = 'overrideNextInvoiceDate',
	XDay = 'xDay',
	LastDayOfMonth = 'lastDayOfMonth',
	Id = 'id',
	Contract = 'contract',
	LastInvoiceGenerationDate = 'lastInvoiceGenerationDate',
	WeekDay = 'weekDay',
	CustomBillingFrequency = 'customBillingFrequency',
	Code = 'code',
	Description = 'description',
	BeginDate = 'beginDate',
	EndDate = 'endDate',
	FinalizedDate = 'finalizedDate',
	ServiceSiteId = 'serviceSiteId',
	ServiceSite = 'serviceSite',
	BaselineItemRequiredFlag = 'baselineItemRequiredFlag',
	BackupRequiredNotes = 'backupRequiredNotes',
	SpecialInvoiceCodingNotes = 'specialInvoiceCodingNotes',
	InvoiceSendMethodNotes = 'invoiceSendMethodNotes',
	RebateCheckNotes = 'rebateCheckNotes',
	DeferredRevenueNotes = 'deferredRevenueNotes',
	InvoiceReviewChecklistNotes = 'invoiceReviewChecklistNotes',
	ContractNotes = 'contractNotes',
	TransactionDataEntryNotes = 'transactionDataEntryNotes',
	NotesType = 'notesType'
}

export enum BillingFrequencyCodeSearchResultFields {
	searchByFrequencyCode = 'searchByFrequencyCode',
	searchByCustomerId = 'searchByCustomerId',
	searchByCustomerName = 'searchByCustomerName',
	searchByServiceSiteId = 'searchByServiceSiteId',
	searchByCompanyId = 'searchByCompanyId',
	searchByBillingAgency = 'searchByBillingAgency'
}

export declare namespace BillingFrequency {
	interface Basic extends Entity {
		frequencyType: BillingFrequencyType
		customer: Entity
		company: Entity
		customBillingFrequencyCode: string
	}

	interface Full {
		[BillingFrequencyLabels.Contract]: string | null
		[BillingFrequencyLabels.Frequency]: BillingFrequencyType
		[BillingFrequencyLabels.GroupByPO]: boolean
		[BillingFrequencyLabels.GroupByServiceSite]: boolean
		[BillingFrequencyLabels.Id]: Key
		[BillingFrequencyLabels.InvoiceGenerationCode]: string
		[BillingFrequencyLabels.InvoiceTypeSendCode]: string
		[BillingFrequencyLabels.LastDayOfMonth]: boolean
		[BillingFrequencyLabels.LastInvoiceGenerationDate]: string
		[BillingFrequencyLabels.Report]: string
		[BillingFrequencyLabels.XDay]: number | null
	}

	interface Info
		extends Omit<
			Full,
			| BillingFrequencyLabels.Report
			| BillingFrequencyLabels.Contract
			| BillingFrequencyLabels.LastInvoiceGenerationDate
		> {
		[BillingFrequencyLabels.Report]: Entity
		[BillingFrequencyLabels.PaymentTerm]: Entity
		[BillingFrequencyLabels.OverrideNextInvoiceDate]: string
		[BillingFrequencyLabels.CustomBillingFrequency]: Custom.Info | null
	}

	type BasicFromId = Entity['id']

	namespace Form {
		interface InvoiceDateOverride {
			invoiceDate: string
			billingFrequencyIds: BasicFromId[]
		}

		namespace Add {
			interface Request {
				[BillingFrequencyLabels.ReportId]: Key | null
				[BillingFrequencyLabels.PaymentTermId]: Key | null
				[BillingFrequencyLabels.GroupByServiceSite]: boolean
				[BillingFrequencyLabels.GroupByPO]: boolean
				[BillingFrequencyLabels.Frequency]: BillingFrequencyType
				[BillingFrequencyLabels.InvoiceGenerationCode]: Key | null
				[BillingFrequencyLabels.InvoiceTypeSendCode]: Key | null
				[BillingFrequencyLabels.OverrideNextInvoiceDate]: string
				[BillingFrequencyLabels.XDay]: number | null
				[BillingFrequencyLabels.LastDayOfMonth]: boolean
				[BillingFrequencyLabels.CustomBillingFrequency]: Custom.Full | null
			}

			type PrepareFields = Omit<
				Request,
				| BillingFrequencyLabels.OverrideNextInvoiceDate
				| BillingFrequencyLabels.CustomBillingFrequency
			> &
				Custom.Form.Add

			interface Basic extends PrepareFields {
				[BillingFrequencyLabels.OverrideNextInvoiceDate]: moment.Moment | null
				[BillingFrequencyLabels.WeekDay]: number
			}
		}
	}

	namespace Search {
		interface Result {
			[BillingFrequencySearchResultFields.searchById]: number[]
			[BillingFrequencySearchResultFields.searchByCompanyId]: Entity[]
			[BillingFrequencySearchResultFields.searchByCustomerId]: Entity[]
			[BillingFrequencySearchResultFields.searchByCompanyName]: Entity[]
			[BillingFrequencySearchResultFields.searchByServiceSite]: Entity[]
			[BillingFrequencySearchResultFields.searchByCustomerName]: Entity[]
			[BillingFrequencySearchResultFields.searchByFrequencyCode]: Entity[]
			[BillingFrequencySearchResultFields.searchByBillingAgency]: Entity[]
		}
	}

	namespace Custom {
		interface Full {
			[BillingFrequencyLabels.Code]: string
			[BillingFrequencyLabels.Description]: string | null
			[BillingFrequencyLabels.BeginDate]: string
			[BillingFrequencyLabels.EndDate]: string | null
			[BillingFrequencyLabels.FinalizedDate]: string | null
			[BillingFrequencyLabels.ServiceSiteId]: Key | null
			[BillingFrequencyLabels.BaselineItemRequiredFlag]: boolean
			[BillingFrequencyLabels.BackupRequiredNotes]: string | null
			[BillingFrequencyLabels.SpecialInvoiceCodingNotes]: string | null
			[BillingFrequencyLabels.InvoiceSendMethodNotes]: string | null
			[BillingFrequencyLabels.RebateCheckNotes]: string | null
			[BillingFrequencyLabels.DeferredRevenueNotes]: string | null
			[BillingFrequencyLabels.InvoiceReviewChecklistNotes]: string | null
			[BillingFrequencyLabels.ContractNotes]: string | null
			[BillingFrequencyLabels.TransactionDataEntryNotes]: string | null
		}

		interface Info extends Omit<Full, BillingFrequencyLabels.ServiceSiteId> {
			[BillingFrequencyLabels.ServiceSite]: Entity | null
		}
		namespace Form {
			interface Add
				extends Omit<
					Full,
					| BillingFrequencyLabels.BeginDate
					| BillingFrequencyLabels.EndDate
					| BillingFrequencyLabels.FinalizedDate
				> {
				[BillingFrequencyLabels.BeginDate]: moment.Moment | null
				[BillingFrequencyLabels.EndDate]: moment.Moment | null
				[BillingFrequencyLabels.FinalizedDate]: moment.Moment | null
				[BillingFrequencyLabels.NotesType]: NotesType | null
			}
		}
	}

	namespace Code {
		namespace Search {
			interface Result {
				[BillingFrequencyCodeSearchResultFields.searchByFrequencyCode]: Entity[]
				[BillingFrequencyCodeSearchResultFields.searchByCustomerId]: Entity[]
				[BillingFrequencyCodeSearchResultFields.searchByCustomerName]: Entity[]
				[BillingFrequencyCodeSearchResultFields.searchByServiceSiteId]: Entity[]
				[BillingFrequencyCodeSearchResultFields.searchByCompanyId]: Entity[]
				[BillingFrequencyCodeSearchResultFields.searchByBillingAgency]: Entity[]
			}
		}
	}
}
